import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnDef,
  functionalUpdate,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Table,
  TableState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import {
  TanstackTableControlledState,
  TanstackTableProps,
  TanstackTableStateValues,
} from './types';
import { defaultFilterFnFor } from './filterFns';

const DEFAULT_STATE: TanstackTableStateValues = {
  sorting: [],
  columnFilters: [],
  globalFilter: '',
  pagination: { pageIndex: 0, pageSize: 10 },
  rowSelection: {},
  expanded: {},
  grouping: [],
  columnVisibility: {},
  columnOrder: [],
  columnPinning: { left: [], right: [] },
  columnSizing: {},
};

/**
 * Builds a TanStack table instance that works identically in `client` and
 * `server` mode. Every feature can be left uncontrolled (client mode owns the
 * state) or controlled via `props.state` + the matching `onXChange` callback
 * (server mode). Function updaters are always resolved against the currently
 * effective value before the consumer callback fires, so callbacks receive a
 * plain value rather than an updater function.
 */
export function useTanstackTable<TData>(props: TanstackTableProps<TData>): Table<TData> {
  const isServer = props.mode === 'server';
  const rowCount = props.mode === 'server' ? props.rowCount : undefined;

  const [internalState, setInternalState] = useState<TanstackTableStateValues>(() => ({
    ...DEFAULT_STATE,
    ...props.initialState,
    pagination: {
      ...DEFAULT_STATE.pagination,
      ...props.initialState?.pagination,
    },
    columnPinning: {
      ...DEFAULT_STATE.columnPinning,
      ...props.initialState?.columnPinning,
    },
  }));

  // The state actually handed to the table: controlled keys (from props.state)
  // win over the internal copy. Only *defined* controlled keys override.
  //
  // `props.pagination` is a second way in, for the same key. It exists because
  // pagination is the one piece of state a *helper* commonly owns while the page
  // owns the rest: sharing `state` would mean two `state` props on one element,
  // where the later spread silently wins and the page's sorting or filters
  // disappear. Given both, the dedicated prop is the more specific intent.
  const effectiveState = useMemo<TanstackTableStateValues>(() => {
    const controlled = props.state ?? {};
    const merged = { ...internalState } as TanstackTableStateValues;
    (Object.keys(controlled) as (keyof TanstackTableControlledState)[]).forEach(
      (key: keyof TanstackTableControlledState) => {
        const value = controlled[key];
        if (value !== undefined) {
          (merged as unknown as Record<string, unknown>)[key as string] = value;
        }
      }
    );
    if (props.pagination !== undefined) merged.pagination = props.pagination;
    return merged;
  }, [internalState, props.state, props.pagination]);

  // Keep the latest effective state in a ref so updaters resolve against the
  // value the table currently sees, even when controlled.
  const effectiveRef = useRef(effectiveState);
  effectiveRef.current = effectiveState;

  const onStateChangeRef = useRef(props.onStateChange);
  onStateChangeRef.current = props.onStateChange;

  const makeHandler = useMemo(() => {
    return <K extends keyof TanstackTableStateValues>(
      key: K,
      callback?: (value: TanstackTableStateValues[K]) => void
    ) => {
      return (updater: Updater<TanstackTableStateValues[K]>): void => {
        const current = effectiveRef.current[key];
        const next = functionalUpdate(updater, current);
        setInternalState((prev: TanstackTableStateValues) => ({ ...prev, [key]: next }));
        callback?.(next);
        onStateChangeRef.current?.({
          ...effectiveRef.current,
          [key]: next,
        } as unknown as TableState);
      };
    };
  }, []);

  const pageCount =
    isServer && rowCount !== undefined
      ? Math.max(1, Math.ceil(rowCount / Math.max(1, effectiveState.pagination.pageSize)))
      : undefined;

  // Hold the previous array while its contents are unchanged.
  //
  // `data={rows ?? []}` — the ordinary way to write a page — hands over a brand
  // new array on every render, and TanStack rebuilds its core row model whenever
  // that identity changes. Comparing contents costs one pass over a page of rows
  // and measurably cuts dropped frames while resizing.
  //
  // It compares element *identity* by index, so it cannot help a page that maps
  // in render (`data={rows.map(toViewModel)}`) — every element is new every
  // time. Such a page still wants `useMemo`. Correctness never depends on this:
  // pagination is not written back from `data` identity (see
  // `autoResetPageIndex` below), so a page that skips memoising only pays for it
  // in render cost.
  const dataRef = useRef(props.data);
  if (
    dataRef.current !== props.data &&
    (dataRef.current.length !== props.data.length ||
      props.data.some((row: TData, index: number) => row !== dataRef.current[index]))
  ) {
    dataRef.current = props.data;
  }
  const data = dataRef.current;

  // Auto-assign a sensible `filterFn` from `meta.filterVariant` when the column
  // does not already declare one, so client-mode filtering "just works".
  const columns = useMemo(() => {
    return props.columns.map((column: ColumnDef<TData, unknown>) => {
      if (column.filterFn || !column.meta?.filterVariant) return column;
      const filterFn = defaultFilterFnFor(column.meta.filterVariant);
      return filterFn ? { ...column, filterFn } : column;
    });
  }, [props.columns]);

  const table = useReactTable<TData>({
    data,
    columns: columns as typeof props.columns,
    state: effectiveState,
    getRowId: props.getRowId,
    getSubRows: props.getSubRows,

    // --- Feature toggles ---
    enableSorting: props.enableSorting ?? true,
    enableMultiSort: props.enableMultiSort ?? true,
    enableFilters: props.enableColumnFilters ?? true,
    enableColumnFilters: props.enableColumnFilters ?? true,
    enableGlobalFilter: props.enableGlobalFilter ?? true,
    enableRowSelection: props.enableRowSelection ?? false,
    enableMultiRowSelection: props.enableMultiRowSelection ?? true,
    enableExpanding: props.enableExpanding ?? Boolean(props.renderSubComponent || props.getSubRows),
    enableGrouping: props.enableGrouping ?? false,
    enableColumnPinning: props.enableColumnPinning ?? false,
    enableHiding: props.enableColumnVisibility ?? true,

    // --- Manual (server) flags ---
    manualSorting: isServer,
    manualFiltering: isServer,
    manualPagination: isServer,
    manualGrouping: isServer,
    manualExpanding: isServer,
    pageCount,
    rowCount,
    // Off deliberately, and replaced below by the two rules it was standing in
    // for. TanStack's version keys off the `data` array's identity, so a page
    // that builds that array in render — `data={rows ?? []}` is enough — is
    // bounced to page 1 on every render and can never leave it. Nothing else
    // here depends on that identity, so pages need not memoise `data`.
    autoResetPageIndex: false,
    autoResetExpanded: false,

    // --- Change handlers ---
    onSortingChange: makeHandler('sorting', props.onSortingChange),
    onColumnFiltersChange: makeHandler('columnFilters', props.onColumnFiltersChange),
    onGlobalFilterChange: makeHandler('globalFilter', props.onGlobalFilterChange),
    onPaginationChange: makeHandler('pagination', props.onPaginationChange),
    onRowSelectionChange: makeHandler('rowSelection', props.onRowSelectionChange),
    onExpandedChange: makeHandler('expanded', props.onExpandedChange),
    onGroupingChange: makeHandler('grouping', props.onGroupingChange),
    onColumnVisibilityChange: makeHandler('columnVisibility', props.onColumnVisibilityChange),
    onColumnOrderChange: makeHandler('columnOrder', props.onColumnOrderChange),
    onColumnPinningChange: makeHandler('columnPinning', props.onColumnPinningChange),
    onColumnSizingChange: makeHandler('columnSizing', props.onColumnSizingChange),

    // --- Row models ---
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(isServer
      ? {}
      : {
          getSortedRowModel: getSortedRowModel(),
          getFilteredRowModel: getFilteredRowModel(),
          // Omitting the pagination row model when pagination is disabled makes
          // the table render every row instead of silently slicing to one page.
          ...(props.enablePagination === false
            ? {}
            : { getPaginationRowModel: getPaginationRowModel() }),
          getGroupedRowModel: getGroupedRowModel(),
          getFacetedRowModel: getFacetedRowModel(),
          getFacetedUniqueValues: getFacetedUniqueValues(),
          getFacetedMinMaxValues: getFacetedMinMaxValues(),
        }),
  });

  const isPaginated = props.enablePagination !== false;

  // What decides which rows exist — as opposed to the `data` array's identity,
  // which decides nothing and changes constantly.
  const resultSetSignature = JSON.stringify([
    effectiveState.columnFilters,
    effectiveState.globalFilter ?? null,
    effectiveState.sorting,
  ]);
  const lastSignatureRef = useRef(resultSetSignature);

  const pageIndex = effectiveState.pagination.pageIndex;
  const lastPageIndex = Math.max(0, table.getPageCount() - 1);

  // The two rules TanStack's `autoResetPageIndex` was standing in for, in one
  // effect because they can want different things on the same commit and the
  // first has to win. Split across two effects, the second would run with this
  // render's stale `pageIndex` and overwrite the first.
  useEffect(() => {
    if (!isPaginated) return;

    // Rule 1 — back to the first page when the result set is *redefined*.
    // Filtering or re-sorting makes "page 5" meaningless: those rows bear no
    // relation to the ones that were there, and someone who has just typed a
    // search wants the first matches, not the tail of them.
    const isRedefined = lastSignatureRef.current !== resultSetSignature;
    lastSignatureRef.current = resultSetSignature;
    if (isRedefined) {
      if (pageIndex !== 0) table.setPageIndex(0);
      return;
    }

    // Rule 2 — never strand the user past the last page. The row count can fall
    // without the result set being redefined: a refetch returning fewer rows, a
    // row deleted elsewhere. Skipped while loading, because a page whose
    // `rowCount` momentarily reads 0 mid-refetch would otherwise yank the user
    // to the first page and refetch from there.
    if (props.isLoading) return;
    if (pageIndex > lastPageIndex) table.setPageIndex(lastPageIndex);
  }, [table, resultSetSignature, pageIndex, lastPageIndex, isPaginated, props.isLoading]);

  return table;
}
